import React from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

/**
 * The order-summary panel on the checkout page.
 *
 * Track 4.9 turned three `<div onClick>` headers into real disclosures and
 * the price rows from `<h4>`/`<h3>` pairs into a `<dl>`, but left the markup
 * inline — the row shape was written out nineteen times and the disclosure
 * three times, chevron and all. These two components are that shape, once.
 */

export interface OrderLineProps {
  term: React.ReactNode;
  value: React.ReactNode;
  /** Discount lines are tinted and read as a credit rather than a charge. */
  variant?: 'discount';
}

/** One term/value row inside a group's `<dl>`. */
export function OrderLine({ term, value, variant }: OrderLineProps): JSX.Element {
  return (
    <div
      className={`pay-order__line${
        variant ? ` pay-order__line--${variant}` : ''
      }`}
    >
      <dt className="pay-order__term">{term}</dt>
      <dd className="pay-order__value">{value}</dd>
    </div>
  );
}

export interface OrderGroupProps {
  /** Identifies the panel; the toggle points `aria-controls` at it. */
  id: string;
  name: React.ReactNode;
  figure: React.ReactNode;
  /**
   * Footnote marker rendered after the name. The note itself lives in
   * `.pay-order__notes` at the foot of the panel.
   */
  asterisk?: '*' | '**';
  /** The amount due today, which carries more visual weight than the rest. */
  primary?: boolean;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

/**
 * A figure that expands to show how it was reached.
 *
 * The button sits *inside* the heading rather than replacing it, so the
 * panel keeps its outline (booking-flow-redesign rule 12), and the lines
 * are hidden with `hidden` rather than `max-height: 0` so they leave the
 * accessibility tree with the paint (rule 7).
 */
export function OrderGroup({
  id,
  name,
  figure,
  asterisk,
  primary,
  open,
  onToggle,
  children,
}: OrderGroupProps): JSX.Element {
  return (
    <div
      className={`pay-order__group${
        primary ? ' pay-order__group--primary' : ''
      }`}
    >
      <h3 className="pay-order__row">
        <button
          type="button"
          className="pay-order__toggle"
          aria-expanded={open}
          aria-controls={id}
          onClick={onToggle}
        >
          <span className="pay-order__name">
            {name}
            {asterisk && (
              <span className="pay-order__asterisk">{asterisk}</span>
            )}
          </span>
          <span className="pay-order__figure">{figure}</span>
          <ChevronDownIcon className="pay-order__chevron" aria-hidden="true" />
        </button>
      </h3>

      <dl className="pay-order__lines" id={id} hidden={!open}>
        {children}
      </dl>
    </div>
  );
}
